import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import * as Cesium from 'cesium';
import { applyTerrainModeToAllVectorLayers } from '../utils/vectorLayerTerrainSync';
import { useDataCoverageOverlay } from '../hooks/useDataCoverageOverlay';
const MapDisplayContext = createContext(undefined);
const LegendOverlayStateContext = createContext(undefined);
const DEFAULT_COVERAGE_OPACITY = 0.2;
// World terrain is on whenever the globe carries anything but the flat
// ellipsoid. Read from the viewer, never assumed, so the toggle cannot
// disagree with what print mode (or anything else) left on the globe.
const isWorldTerrainOn = (viewer) => !(viewer.terrainProvider instanceof Cesium.EllipsoidTerrainProvider);
export const MapOverlayStateProvider = ({ viewer, coverageSuspended, children, }) => {
    const [terrainEnabled, setTerrainEnabled] = useState(() => !!viewer && !viewer.isDestroyed() && isWorldTerrainOn(viewer));
    const [terrainLoading, setTerrainLoading] = useState(false);
    useEffect(() => {
        if (!viewer || viewer.isDestroyed()) {
            setTerrainEnabled(false);
            return;
        }
        const sync = () => {
            if (!viewer.isDestroyed())
                setTerrainEnabled(isWorldTerrainOn(viewer));
        };
        sync();
        return viewer.scene.globe.terrainProviderChanged.addEventListener(sync);
    }, [viewer]);
    const toggleTerrain = useCallback(async () => {
        if (!viewer || viewer.isDestroyed() || terrainLoading)
            return;
        if (isWorldTerrainOn(viewer)) {
            viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
            viewer.scene.globe.depthTestAgainstTerrain = false;
            applyTerrainModeToAllVectorLayers(viewer, false);
            return;
        }
        setTerrainLoading(true);
        try {
            const terrain = await Cesium.createWorldTerrainAsync();
            if (viewer.isDestroyed())
                return;
            viewer.terrainProvider = terrain;
            viewer.scene.globe.depthTestAgainstTerrain = true;
            applyTerrainModeToAllVectorLayers(viewer, true);
        }
        catch (e) {
            console.error('Failed to load Cesium World Terrain', e);
        }
        finally {
            setTerrainLoading(false);
        }
    }, [viewer, terrainLoading]);
    const [coverageVisible, setCoverageVisible] = useState(false);
    const [coverageOpacity, setCoverageOpacity] = useState(DEFAULT_COVERAGE_OPACITY);
    const [coverageHover, setCoverageHover] = useState(null);
    useDataCoverageOverlay(viewer, {
        visible: coverageVisible && !coverageSuspended,
        opacity: coverageOpacity,
        onHover: setCoverageHover,
    });
    const [isExpanded, setIsExpanded] = useState(false);
    const [statsByProjectId, setStatsByProjectId] = useState({});
    const artifactCountsRef = useRef({});
    const mapDisplay = useMemo(() => ({
        terrainEnabled,
        terrainLoading,
        toggleTerrain,
        coverageVisible,
        setCoverageVisible,
        coverageOpacity,
        setCoverageOpacity,
        coverageHover,
    }), [terrainEnabled, terrainLoading, toggleTerrain, coverageVisible, coverageOpacity, coverageHover]);
    const legend = useMemo(() => ({ isExpanded, setIsExpanded, statsByProjectId, setStatsByProjectId, artifactCountsRef }), [isExpanded, statsByProjectId]);
    return (_jsx(MapDisplayContext.Provider, { value: mapDisplay, children: _jsx(LegendOverlayStateContext.Provider, { value: legend, children: children }) }));
};
export const useMapDisplayState = () => {
    const ctx = useContext(MapDisplayContext);
    if (ctx === undefined) {
        throw new Error('useMapDisplayState must be used within a MapOverlayStateProvider');
    }
    return ctx;
};
export const useLegendOverlayState = () => {
    const ctx = useContext(LegendOverlayStateContext);
    if (ctx === undefined) {
        throw new Error('useLegendOverlayState must be used within a MapOverlayStateProvider');
    }
    return ctx;
};
